import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLAnchorElement|HTMLButtonElement} [showMoreButton] - The "Show More Products" button.
 * @property {HTMLElement} [showMoreWrapper] - The wrapper containing the Show More button and loader.
 * @property {HTMLElement} [showMoreLoader] - The loader shown while loading more products.
 * @property {HTMLParagraphElement} [resultsCountText] - The "You saw X out of Y results" element.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #showMoreLoading = false;

  /** After first "Show More" click: keep CTA hidden while more products load via scroll (reset on filter). */
  #lazyAfterShowMore = false;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /** @type {number} */
  #infiniteLoadingCount = 0;

  /** @type {number | null} */
  #nextLoadTimeoutId = null;

  /** Number of pages to fetch and append per batch for Show More (shows results sooner). */
  static #SHOW_MORE_BATCH_SIZE = 1;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#setupShowMoreButton();
    this.#updateShowMoreUI();
    this.#setInfiniteLoaderVisibility();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.#nextLoadTimeoutId != null) {
      clearTimeout(this.#nextLoadTimeoutId);
      this.#nextLoadTimeoutId = null;
    }
    this.refs.showMoreButton?.removeEventListener('click', this.#handleShowMoreClick);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #setupShowMoreButton() {
    const { showMoreButton } = this.refs;
    if (!showMoreButton) return;
    showMoreButton.addEventListener('click', this.#handleShowMoreClick);
  }

  /**
   * @returns {boolean} True when the Show More link is present and visible (suppresses infinite-scroll loader / URL pushes).
   */
  #isShowMoreCtaVisible() {
    const b = this.refs.showMoreButton;
    return Boolean(b && !b.hidden);
  }

  /**
   * Strip `page` from a URL for the address bar. Section fetches still pass `?page=` via
   * {@link #getPage} / {@link #fetchSpecificPage}; only history is kept without pagination noise.
   * @param {URL} url
   * @returns {string}
   */
  #urlWithoutPageSearchParam(url) {
    const clean = new URL(url.href);
    clean.searchParams.delete('page');
    return clean.toString();
  }

  #handleShowMoreClick = async () => {
    if (this.#showMoreLoading) return;
    const { showMoreButton, showMoreWrapper, showMoreLoader, grid } = this.refs;
    if (!showMoreButton || !grid) return;
    showMoreButton.hidden = true;
    if (showMoreWrapper) {
      showMoreWrapper.setAttribute('data-show-more-state', 'loading');
    }
    if (showMoreLoader) {
      showMoreLoader.hidden = false;
      showMoreLoader.setAttribute('aria-hidden', 'false');
    }
    this.#showMoreLoading = true;
    if (showMoreButton instanceof HTMLButtonElement) {
      showMoreButton.disabled = true;
    }
    try {
      const lastPage = Number(grid.dataset.lastPage) || 1;
      if (lastPage < 2) {
        showMoreButton.hidden = false;
        return;
      }
      const startPage = 2;
      const endPage = Math.min(startPage + PaginatedList.#SHOW_MORE_BATCH_SIZE - 1, lastPage);
      await this.#prefetchPages(startPage, endPage);
      this.#appendPagesToGrid(startPage, endPage);
      this.#aspectRatioHelper?.processNewElements();
      if (!this.#hasPageRendered(startPage)) {
        throw new Error('Show more: no items appended for next page');
      }
      this.#lazyAfterShowMore = true;
      this.#updateShowMoreUI();
      const { viewMoreNext } = this.refs;
      if (viewMoreNext) {
        viewMoreNext.hidden = false;
        viewMoreNext.setAttribute('aria-hidden', 'false');
      }
      this.#observeViewMore();
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } catch (error) {
      console.error(error);
      this.#lazyAfterShowMore = false;
      showMoreButton.hidden = false;
    } finally {
      this.#showMoreLoading = false;
      if (showMoreButton instanceof HTMLButtonElement) {
        showMoreButton.disabled = false;
      }
      if (this.#lazyAfterShowMore) {
        this.#setInfiniteLoaderVisibility();
        if (showMoreWrapper) {
          showMoreWrapper.setAttribute(
            'data-show-more-state',
            this.#infiniteLoadingCount > 0 ? 'loading' : 'idle'
          );
        }
      } else {
        if (showMoreLoader) {
          showMoreLoader.hidden = true;
          showMoreLoader.setAttribute('aria-hidden', 'true');
        }
        if (showMoreWrapper) {
          showMoreWrapper.setAttribute('data-show-more-state', 'idle');
        }
      }
    }
  };

  /**
   * Prefetch pages from start to end (inclusive) in parallel so they are ready when appending.
   * @param {number} startPage
   * @param {number} endPage
   */
  async #prefetchPages(startPage, endPage) {
    const promises = [];
    for (let p = startPage; p <= endPage; p++) {
      const pageInfo = { page: p, url: new URL(window.location.href) };
      pageInfo.url.searchParams.set('page', String(p));
      pageInfo.url.hash = '';
      if (this.#shouldUsePage(pageInfo)) {
        promises.push(this.#fetchSpecificPage(p, pageInfo.url));
      }
    }
    await Promise.all(promises);
  }

  /**
   * Append all items from pages startPage..endPage to the grid in one DOM update.
   * @param {number} startPage
   * @param {number} endPage
   */
  #appendPagesToGrid(startPage, endPage) {
    const { grid } = this.refs;
    if (!grid) return;
    const fragment = document.createDocumentFragment();
    for (let p = startPage; p <= endPage; p++) {
      const items = this.#getGridForPage(p);
      if (items) {
        fragment.append(...items);
      }
    }
    grid.append(fragment);
  }

  #updateShowMoreUI() {
    const { grid, resultsCountText } = this.refs;
    if (!resultsCountText) return;
    const total = Number(resultsCountText.dataset.total) || 0;
    const count = grid ? grid.querySelectorAll('[ref="cards[]"]').length : 0;
    const format = resultsCountText.dataset.format;
    if (format) {
      resultsCountText.textContent = format.replace('__COUNT__', String(count)).replace('__TOTAL__', String(total));
    }
    const allShown = total > 0 && count >= total;
    const showMoreButton = this.refs.showMoreButton;
    if (showMoreButton) {
      showMoreButton.hidden = allShown || this.#lazyAfterShowMore;
    }
  }

  #setInfiniteLoaderVisibility() {
    const { showMoreLoader, showMoreButton } = this.refs;
    if (!showMoreLoader) return;
    if (showMoreButton && !showMoreButton.hidden) return;

    const isVisible = this.#infiniteLoadingCount > 0;
    showMoreLoader.hidden = !isVisible;
    showMoreLoader.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  #startInfiniteLoading() {
    const { showMoreLoader, showMoreButton } = this.refs;
    if (!showMoreLoader) return;
    if (showMoreButton && !showMoreButton.hidden) return;

    this.#infiniteLoadingCount += 1;
    this.#setInfiniteLoaderVisibility();
  }

  #finishInfiniteLoading() {
    const { showMoreLoader, showMoreButton } = this.refs;
    if (!showMoreLoader) return;
    if (showMoreButton && !showMoreButton.hidden) return;

    this.#infiniteLoadingCount = Math.max(0, this.#infiniteLoadingCount - 1);
    this.#setInfiniteLoaderVisibility();
  }

  /**
   * @param {Element | undefined} element
   * @param {number} [offset]
   * @returns {boolean}
   */
  #isElementNearViewport(element, offset = 200) {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    return rect.top <= window.innerHeight + offset && rect.bottom >= -offset;
  }

  #hasNextPage() {
    return this.#shouldUsePage(this.#getPage('next'));
  }

  #stopObservingNextPage() {
    if (!this.infinityScrollObserver) return;
    const { viewMoreNext } = this.refs;
    if (viewMoreNext) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }
  }

  /**
   * @param {number} [delay]
   */
  #scheduleNextPageLoad(delay = 0) {
    if (this.#nextLoadTimeoutId != null) return;
    this.#nextLoadTimeoutId = window.setTimeout(() => {
      this.#nextLoadTimeoutId = null;
      this.#renderNextPage();
    }, delay);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error(error);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#isRenderingNextPage) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#stopObservingNextPage();
      return;
    }
    if (this.#hasPageRendered(nextPage.page)) {
      if (this.#isElementNearViewport(this.refs.viewMoreNext) && this.#hasNextPage()) {
        this.#scheduleNextPageLoad();
      }
      return;
    }

    this.#isRenderingNextPage = true;
    this.#startInfiniteLoading();
    let shouldContinueLoading = false;
    let shouldRetry = false;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);
        nextPageItemElements = this.#getGridForPage(nextPage.page);
      }

      if (!nextPageItemElements || nextPageItemElements.length === 0) {
        shouldRetry = this.#isElementNearViewport(this.refs.viewMoreNext) && this.#hasNextPage();
        return;
      }

      if (this.#hasPageRendered(nextPage.page)) return;

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      if (!this.#isShowMoreCtaVisible() && this.shouldPushPageUrl) {
        history.replaceState(history.state, '', this.#urlWithoutPageSearchParam(nextPage.url));
      }

      this.#updateShowMoreUI();

      if (this.#isShowMoreCtaVisible()) {
        this.#fetchPage('next');
      } else {
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
      }

      if (!this.#hasNextPage()) {
        this.#stopObservingNextPage();
      } else if (this.#isElementNearViewport(this.refs.viewMoreNext)) {
        // Keep loading sequential pages while the sentinel is still in view.
        shouldContinueLoading = true;
      }
    } catch (error) {
      console.error(error);
      shouldRetry = this.#isElementNearViewport(this.refs.viewMoreNext) && this.#hasNextPage();
    } finally {
      this.#finishInfiniteLoading();
      this.#isRenderingNextPage = false;
    }

    if (shouldContinueLoading) {
      this.#scheduleNextPageLoad();
    } else if (shouldRetry) {
      this.#scheduleNextPageLoad(800);
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.#isRenderingPreviousPage) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    if (this.#hasPageRendered(previousPage.page)) return;

    this.#isRenderingPreviousPage = true;
    this.#startInfiniteLoading();

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      if (this.#hasPageRendered(previousPage.page)) return;

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      if (!this.#isShowMoreCtaVisible() && this.shouldPushPageUrl) {
        history.replaceState(history.state, '', this.#urlWithoutPageSearchParam(previousPage.url));
      }

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.#finishInfiniteLoading();
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    const cardPageElements = grid.querySelectorAll('[ref="cards[]"][data-page]');
    if (!cardPageElements.length) return;

    let currentCardPage = isPrevious ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;

    for (const card of cardPageElements) {
      const pageValue = Number(card.getAttribute('data-page'));
      if (!Number.isFinite(pageValue)) continue;
      currentCardPage = isPrevious ? Math.min(currentCardPage, pageValue) : Math.max(currentCardPage, pageValue);
    }

    if (!Number.isFinite(currentCardPage)) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {boolean}
   */
  #hasPageRendered(page) {
    const { grid } = this.refs;
    if (!grid) return false;

    return Boolean(grid.querySelector(`[ref="cards[]"][data-page="${page}"]`));
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Get all direct grid children to preserve order across list structures.
    return gridElement.querySelectorAll(':scope > *');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  get shouldPushPageUrl() {
    return true;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#lazyAfterShowMore = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered/sorted content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const lastPageChanged = newLastPage !== currentLastPage;
      const gridContentChanged = mutations.some((m) => m.type === 'childList');

      // Run setup when data-last-page changed OR grid content changed (e.g. after sorting)
      if (lastPageChanged || gridContentChanged) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#setupShowMoreButton();
        this.#updateShowMoreUI();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
